// JS
import React, { useState, useEffect } from 'react';
import {
  renderHTMLspan,
  renderHTML,
  swapImg,
  ppHeadingOverridesClose,
  ppPopupOverridesClose
} from '../shared/utils';

// NUQLIUM API SETTINGS
import { miniBasketNQApiFunc } from '../shared/jsonData';

// COMP
import Button from '../Button';

// COMPONENT
const MiniBasketPreview = ({
  miniBasketProdId,
  setMiniBasketProdId,
  showHideMiniBasket,
  setShowHideMiniBasket,
  userSession,
  miniBasketSize,
  miniBasketProdSku,
  closeQuickViewCleanUp,
  QVMBPopup = false,
  setSizeSelect,
  setProdSku
}) => {
  const uniqueId = Number(new Date());
  const [data, setData] = useState(null);
  const [nuqliumApi, setNuqliumApi] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [fadeProp, setFadeProp] = useState({ fade: 'animate-fadeIn' });

  // Modal - Scroll & No Scroll
  const [modalScroll, setModalScroll] = useState(false);

  const scrollModalClosed = () => {
    setModalScroll(false);
    // Scroll - Modal Closed
    document.body.style.overflow = 'scroll';
  };

  const scrollModalShow = () => {
    setModalScroll(true);
    // No Scroll - Modal Open
    if (typeof window != 'undefined' && window.document) {
      document.body.style.overflow = 'hidden';
    }
  };

  useEffect(() => {
    if (miniBasketProdId) {
      setNuqliumApi(miniBasketNQApiFunc(miniBasketProdId));
    }
  }, [miniBasketProdId]);

  useEffect(() => {
    if (nuqliumApi) {
      fetch(nuqliumApi.endPointUrl, {
        method: 'POST',
        body: nuqliumApi.endPointPrams
      })
        .then((response) => response.json())
        .then((data) => {
          if (data && data?.data?.products?.products[0]) {
            setData(data.data.products.products[0]);
            setCurrency(data.data.pricelist.currency);
            scrollModalShow();
          }
        });
    }
  }, [nuqliumApi]);

  const closeMiniBasketCleanUp = () => {
    setShowHideMiniBasket(false);
    setData(null);
    setMiniBasketProdId(null);
    setSizeSelect(null);
    setProdSku(null);
    scrollModalClosed();
    ppPopupOverridesClose();
    if (!QVMBPopup) {
      ppHeadingOverridesClose();
    }
  };

  const checkForColour = (data) => {
    if (data.facolour) {
      return data.facolour[0].toLowerCase();
    } else if (data.colour) {
      return data.colour[0].toLowerCase();
    }
  };

  // No Interaction - Hide Mini Basket Preview after 5 seconds
  useEffect(() => {
    const timeoutFade = setTimeout(() => {
      if (fadeProp.fade === 'animate-fadeIn') {
        document.getElementById('mini-basket-preview').className =
          'animate-fadeOut';
      } else {
        setFadeProp({
          fade: 'animate-fadeIn'
        });
      }
    }, 5000);

    const timeoutRemove = setTimeout(() => {
      setShowHideMiniBasket(false);
      scrollModalClosed();
      ppPopupOverridesClose();
      if (!QVMBPopup) {
        ppHeadingOverridesClose();
      }
    }, 5400);

    return () => clearTimeout(timeoutFade, timeoutRemove);
  }, [fadeProp, setShowHideMiniBasket, QVMBPopup, closeQuickViewCleanUp]);

  return (
    <>
      {showHideMiniBasket && modalScroll && data && (
        <div
          id="mini-basket-preview"
          className={fadeProp.fade}
          onClick={(e) => {
            //Mini Basket Popup - Click Outside - Close
            if (e.currentTarget === e.target) {
              setTimeout(() => {
                closeMiniBasketCleanUp();
              }, '400');
              document.getElementById('mini-basket-preview').className =
                'animate-fadeOut';
            }
          }}
        >
          <div className={`mini-basket-preview-content`} id="fadeInWrapper">
            <div
              id="mini-basket-preview-close-button"
              onClick={(e) => {
                //Mini Basket Popup - Close
                setTimeout(() => {
                  closeMiniBasketCleanUp();
                }, '400');
                document.getElementById('mini-basket-preview').className =
                  'animate-fadeOut';
              }}
            >
              {renderHTML(null, 'close')}
            </div>
            <div className="mini-basket-preview-container">
              <div className="mini-basket-preview-column">
                <h3 className="mini-basket-preview-product-title">
                  <span className="mbp-added">ADDED.</span>
                </h3>
                {/* PRODUCT DETAILS */}
                <div className="mini-basket-preview-row">
                  <div className="mini-basket-preview-img">
                    <img
                      alt={data.product.name}
                      src={swapImg(
                        data.product.image.replace('medium', 'small')
                      )}
                    />
                  </div>
                  <div className="mini-basket-preview-details">
                    <div className="flex flex-col w-2/3 justify-evenly">
                      <h3 className="mini-basket-preview-product-title">
                        <span className="mbp-brand-name">
                          {data.product.attributes.brand}
                        </span>
                        <span className="mbp-product-name">
                          {data.product.name}
                        </span>
                      </h3>
                      <p className="mini-basket-preview-product-colour">
                        {checkForColour(data.product.attributes)}
                      </p>
                      <p className="mini-basket-preview-product-colour">
                        Size: {miniBasketSize}
                      </p>
                    </div>

                    {data.product.option.price ===
                    data.product.option.promotionprice ? (
                      <div className="flex flex-col justify-between w-1/3">
                        <div className="mini-basket-preview-product-stock">
                          In Stock
                        </div>
                        <div className="product-price flex justify-end">
                          {renderHTMLspan(data.product.option.price)}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col justify-between w-1/3">
                        <div className="mini-basket-preview-product-stock">
                          In Stock
                        </div>
                        <div className="product-price flex flex-col items-end">
                          <div className="rrp">
                            {renderHTMLspan(data.product.option.price)}
                          </div>
                          <div className="sale">
                            {renderHTMLspan(data.product.option.promotionprice)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* PRODUCT DETAILS */}
                <Button
                  gaAriaLabel="Checkout Securely"
                  gaDataType="seven-pdp-mini-basket"
                  gaBlockId={data.product.productid}
                  gaProdSku={miniBasketProdSku}
                  gaList="Product Page"
                  gaImage={data.product.image}
                  gaBrand={data.product.attributes.brand}
                  gaCategory={data.product.attributes.product_category[0]}
                  gaTitle={data.product.name}
                  gaName={data.product.name}
                  gaPrice={data.product.option.priceasnumber}
                  gaPromoPrice={data.product.option.promotionpriceasnumber}
                  gaQuantity={1}
                  gaVariant={
                    data.product.attributes.colour &&
                    data.product.attributes.colour[0]
                  }
                  gaCurrency={currency}
                  gaSale={data.product.option.onsale}
                  gaDisabled={!data.product.instock}
                  buttonSlotNumber={1}
                  id={'button-id-' + uniqueId}
                  buttonColour="black"
                  buttonLinkContent="Checkout Securely"
                  setClick={false}
                  buttonLink={'/basket/' + userSession}
                />
                <Button
                  gaAriaLabel="Continue Shopping"
                  gaDataType="seven-pdp-mini-basket"
                  gaBlockId={data.product.productid}
                  gaProdSku={miniBasketProdSku}
                  gaList="Product Page"
                  gaImage={data.product.image}
                  gaBrand={data.product.attributes.brand}
                  gaCategory={data.product.attributes.product_category[0]}
                  gaTitle={data.product.name}
                  gaName={data.product.name}
                  gaPrice={data.product.option.priceasnumber}
                  gaPromoPrice={data.product.option.promotionpriceasnumber}
                  gaQuantity={1}
                  gaVariant={
                    data.product.attributes.colour &&
                    data.product.attributes.colour[0]
                  }
                  gaCurrency={currency}
                  gaSale={data.product.option.onsale}
                  gaDisabled={!data.product.instock}
                  buttonSlotNumber={2}
                  id={'button-id-' + uniqueId}
                  buttonColour="white"
                  buttonLinkContent="Continue Shopping"
                  setClick={true}
                  clickProperties={(e) => {
                    //Mini Basket Popup - Close
                    if (e.currentTarget === e.target) {
                      setTimeout(() => {
                        closeMiniBasketCleanUp();
                      }, '400');
                      document.getElementById('mini-basket-preview').className =
                        'animate-fadeOut';
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MiniBasketPreview;
